import React, { useMemo, useCallback, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { Button, RegWallContainer, TrackableLink } from '@surfline/quiver-react';
import { color } from '@surfline/quiver-themes';
import {
  capitalizeFirstLetter,
  getMeterRemaining,
  trackClickedSubscribeCTA,
  trackEvent,
  SL_FREE_TRIAL,
  TABLET_LARGE_WIDTH,
} from '@surfline/web-common';

import config from '../../../../config';
import {
  SERVED_POPUP,
  CLICKED_POPUP,
  RATE_LIMITING,
  RL_VERIFY_EMAIL_CLICKED,
} from '../../../../common/constants';
import {
  usePromotionEntitlementsSelector,
  useUserDetailsSelector,
} from '../../../../selectors/user';

const clickedVerifyEmailProperties = {
  action: RL_VERIFY_EMAIL_CLICKED,
  category: RATE_LIMITING,
};

/**
 * @param {boolean} isEmailVerified
 */
const getSubtitleTwoClassName = (isEmailVerified) =>
  classNames({
    'sl-meter-premium-paywall__subtitle-two': true,
    'sl-meter-premium-paywall__subtitle-two--verified': isEmailVerified,
  });

const MeterPremiumPaywall = () => {
  const verifyEmailLinkRef = useRef();
  const meterRemaining = useSelector(getMeterRemaining);
  const { isEmailVerified, firstName } = useUserDetailsSelector();
  const promotionEntitlements = usePromotionEntitlementsSelector();

  const isFreeTrialEligible = useMemo(() => promotionEntitlements.includes(SL_FREE_TRIAL), [
    promotionEntitlements,
  ]);

  const ctaButtonText = isFreeTrialEligible ? 'START FREE TRIAL' : 'GO PREMIUM';

  useEffect(() => {
    trackEvent(SERVED_POPUP, {
      popupName: isEmailVerified ? 'Rate Limiting Paywall' : 'Verify Paywall',
      category: RATE_LIMITING,
      platform: 'web',
      isMobileView: window.innerWidth <= TABLET_LARGE_WIDTH,
    });
  }, [isEmailVerified]);

  const onClickCTA = useCallback(() => {
    trackEvent(CLICKED_POPUP, {
      action: 'Clicked CTA',
      category: isEmailVerified ? 'Rate Limiting Paywall' : 'Verify Paywall',
    });

    trackClickedSubscribeCTA({
      location: isEmailVerified ? 'Rate Limiting Paywall' : 'Verify Paywall',
      checksRemaining: meterRemaining,
      platform: 'web',
    });
    setTimeout(() => window.location.assign(config.funnelUrl), 300);
  }, [meterRemaining, isEmailVerified]);

  const editProfileLink = `${config.surflineHost}/account/edit-profile`;

  let headerText = `${
    firstName ? `${capitalizeFirstLetter(firstName)}, you've` : "You've"
  } run out of Surf Checks`;
  if (isEmailVerified) {
    headerText += ' this week';
  }

  const subHeaderText = 'You get 3 new surf checks on Monday.';

  return meterRemaining !== null ? (
    <div className="sl-meter-premium-paywall__container">
      <RegWallContainer bgColor={color('dark-blue', 90)}>
        <div className="sl-meter-premium-paywall__content">
          <div className="sl-meter-premium-paywall__box-header">
            <div className="sl-meter-premium-paywall__count">
              <span className="sl-meter-premium-paywall__count--text">0</span>
            </div>
            <p className="sl-meter-premium-paywall__count-message">Surf Checks Left</p>
          </div>
          <h5 className="sl-meter-premium-paywall__title">{headerText}</h5>
          {isEmailVerified && <p className="sl-meter-premium-paywall__subtitle">{subHeaderText}</p>}
          <p className={getSubtitleTwoClassName(isEmailVerified)}>
            A Premium account will get you back in action with <br />
            unlimited Surf Checks, ad-free cams, <br />
            long range forecasts and more!
          </p>
          <Button className="sl-meter-premium-paywall__actions" onClick={onClickCTA}>
            {ctaButtonText}
          </Button>
          {!isEmailVerified && (
            <div className="sl-meter-premium-paywall__verify-email__text">
              <TrackableLink
                ref={verifyEmailLinkRef}
                eventName={CLICKED_POPUP}
                eventProperties={clickedVerifyEmailProperties}
              >
                <span>Not ready for premium? </span>
                <a ref={verifyEmailLinkRef} href={editProfileLink}>
                  Verify your email
                </a>
                <span>to get weekly surf checks.</span>
              </TrackableLink>
            </div>
          )}
          <div className="sl-meter-premium-paywall__actions--mobile">
            <Button onClick={onClickCTA}>
              <p>{ctaButtonText}</p>
            </Button>
          </div>
        </div>
      </RegWallContainer>
    </div>
  ) : null;
};

export default MeterPremiumPaywall;
